import type { VCSHost } from '../../../../shared/projectsV2/vcs';
import type { ProjectType } from '../../../../typings/enums';
import type { Project, Revision } from '../../../types';
import { IconText, icons } from '../../generic/Icon';
import styles from './ProjectIcons.module.less';

type ProjectIconsProps = {
  project: Project;
  revision?: Revision;
  onNavigateProjectHome: () => void;
  onNavigateRevisionLink: () => void;
};

type ProjectIconPredicates = {
  shouldHavePublicIcon: boolean;
  shouldHavePrivateIcon: boolean;
  shouldHaveGitHubAppIcon: boolean;
  shouldHaveGitHubOAuthIcon: boolean;
  shouldHaveGitLabIcon: boolean;
  shouldHaveBitBucketIcon: boolean;
  shouldHaveBitBucketServerIcon: boolean;
  shouldHaveAzureReposIcon: boolean;
  shouldHaveGitOtherIcon: boolean;
  shouldHaveArchiveIcon: boolean;
  shouldHaveContainerIcon: boolean;
  shouldHaveCliIcon: boolean;
  shouldHaveSbomIcon: boolean;
  shouldHaveBinaryIcon: boolean;
  shouldHaveHomepageIcon: boolean;
  shouldHaveRevisionLinkIcon: boolean;
};

/**
 * Computes the predicate properties needed for ProjectIcons based on the project and revision, and returns their combination.
 * This is only exported in practice so that it may be unit tested.
 *
 * @param {ProjectType} projectType - The type of the project.
 * @param {VCSHost} [vcsHost] - The VCS host of the project.
 * @param {Revision} [revision] - The revision, if any, for getting the link icon if needed.
 * @param {string} [projectLocator] - The project locator.
 * @param {boolean} [isPublic] - Whether the project is public.
 * @param {string} [projectUrl] - The project URL.
 * @returns {ProjectIconPredicates} The predicates/booleans about the icons.
 */
export function projectIconsPredicates(
  projectType: ProjectType,
  vcsHost?: VCSHost,
  revision?: Revision,
  projectLocator?: string,
  isPublic?: boolean,
  projectUrl?: string,
): ProjectIconPredicates {
  const shouldHavePublicIcon = !!isPublic;
  const shouldHavePrivateIcon = !shouldHavePublicIcon;
  const shouldHaveGitHubAppIcon = vcsHost === 'github-app';
  const shouldHaveGitHubOAuthIcon = vcsHost === 'github';
  const shouldHaveGitLabIcon = vcsHost === 'gitlab';
  const shouldHaveBitBucketIcon = vcsHost === 'bitbucket_cloud';
  const shouldHaveBitBucketServerIcon = vcsHost === 'bitbucket_server';
  const shouldHaveAzureReposIcon = vcsHost === 'azure_repos';
  const shouldHaveGitOtherIcon = !(
    shouldHaveGitHubAppIcon
    || shouldHaveGitHubOAuthIcon
    || shouldHaveGitLabIcon
    || shouldHaveBitBucketIcon
    || shouldHaveBitBucketServerIcon
    || shouldHaveAzureReposIcon
  ) && !!(
    projectType === 'autobuild'
    && shouldHavePrivateIcon
    && projectLocator?.startsWith('git')
  );
  const shouldHaveArchiveIcon = projectType === 'archive';
  const shouldHaveContainerIcon = projectType === 'container';
  const shouldHaveCliIcon = projectType === 'provided';
  const shouldHaveSbomIcon = projectType === 'sbom';
  const shouldHaveHomepageIcon = !!projectUrl;
  const shouldHaveBinaryIcon = projectType === 'binary';
  const shouldHaveRevisionLinkIcon = !!(revision?.link);
  return {
    shouldHavePublicIcon,
    shouldHavePrivateIcon,
    shouldHaveGitHubAppIcon,
    shouldHaveGitHubOAuthIcon,
    shouldHaveGitLabIcon,
    shouldHaveBitBucketIcon,
    shouldHaveBitBucketServerIcon,
    shouldHaveAzureReposIcon,
    shouldHaveGitOtherIcon,
    shouldHaveArchiveIcon,
    shouldHaveContainerIcon,
    shouldHaveCliIcon,
    shouldHaveSbomIcon,
    shouldHaveBinaryIcon,
    shouldHaveHomepageIcon,
    shouldHaveRevisionLinkIcon,
  };
}

const ProjectIcons = (props: ProjectIconsProps) => {
  const {
    project: {
      type: projectType,
      locator: projectLocator,
      public: isPublic,
      vcs_host: vcsHost,
      url: projectUrl,
    },
    revision,
    onNavigateProjectHome,
    onNavigateRevisionLink,
  } = props;
  const {
    shouldHavePublicIcon,
    shouldHavePrivateIcon,
    shouldHaveGitHubAppIcon,
    shouldHaveGitHubOAuthIcon,
    shouldHaveGitLabIcon,
    shouldHaveBitBucketIcon,
    shouldHaveBitBucketServerIcon,
    shouldHaveAzureReposIcon,
    shouldHaveGitOtherIcon,
    shouldHaveArchiveIcon,
    shouldHaveContainerIcon,
    shouldHaveCliIcon,
    shouldHaveSbomIcon,
    shouldHaveBinaryIcon,
    shouldHaveHomepageIcon,
    shouldHaveRevisionLinkIcon,
  } = projectIconsPredicates(projectType, vcsHost, revision, projectLocator, isPublic, projectUrl);

  return (
    <div className={styles.projectIcons}>
      {shouldHaveGitHubAppIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.github} text="GitHub App" />
        </span>
      )}

      {shouldHaveGitHubOAuthIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.github} text="GitHub OAuth" />
        </span>
      )}

      {shouldHaveGitLabIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.gitlab} text="GitLab" />
        </span>
      )}

      {shouldHaveGitOtherIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.earth} text="Git (Other/Private)" />
        </span>
      )}

      {shouldHaveBitBucketIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.bitbucket} text="Bitbucket.org" />
        </span>
      )}

      {shouldHaveBitBucketServerIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.bitbucket} text="Bitbucket Server" />
        </span>
      )}

      {shouldHaveAzureReposIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.microsoft} text="Azure Repos" />
        </span>
      )}

      {shouldHaveArchiveIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.zipFileO} text="Archive" />
        </span>
      )}

      {shouldHaveContainerIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.containerO} text="Container" />
        </span>
      )}

      {shouldHaveCliIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.terminal} text="CLI" />
        </span>
      )}

      {shouldHaveSbomIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.listTreeO} text="SBOM" />
        </span>
      )}

      {shouldHaveBinaryIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.fileBinary} text="Binary" />
        </span>
      )}

      {shouldHavePublicIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.lockOpenO} text="Public" />
        </span>
      )}

      {shouldHavePrivateIcon && (
        <span className={styles.navIconTooltip}>
          <IconText icon={icons.lockOutline} text="Private" />
        </span>
      )}

      {shouldHaveHomepageIcon && (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <a onClick={onNavigateProjectHome}>
          <span className={styles.navIconTooltip}>
            <IconText icon={icons.homeO} text="Project Homepage" />
          </span>
        </a>
      )}

      {shouldHaveRevisionLinkIcon && (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <a onClick={onNavigateRevisionLink}>
          <span className={styles.navIconTooltip}>
            <IconText icon={icons.linkO} text="Revision" />
          </span>
        </a>
      )}
    </div>
  );
};

export default ProjectIcons;
